import { useId, type ReactNode } from 'react';
import { Button } from '@/shared/components';
import { cn } from '@/shared/lib/cn';

interface Props {
  title: string;
  /** Rechts im Kopf: der Bearbeiten-Weg der Karte oder ein Zustand. */
  aside?: ReactNode;
  /** Unter einer Linie: was aus dem Inhalt folgt, etwa für wen er gilt. */
  footer?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Eine Karte eines Objekt-Bereichs — Kopf, Inhalt und abgesetzter Fuß mit denselben Kanten. */
export function AreaCard({ title, aside, footer, className, children }: Props) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'border-line/50 bg-card shadow-card flex min-w-0 flex-col rounded-lg border p-5',
        className,
      )}
    >
      <div className="flex min-h-11 flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h3 id={headingId} className="text-ink font-serif text-[19px] leading-tight">
          {title}
        </h3>
        {aside}
      </div>
      <div className="mt-2 flex-1">{children}</div>
      {footer && (
        <div className="border-line/60 text-ink-soft mt-4 space-y-2 border-t pt-3.5 text-[13px]">
          {footer}
        </div>
      )}
    </section>
  );
}

/** Der Bearbeiten-Knopf einer Karte; vorgelesen sagt `label`, welche Karte gemeint ist. */
export function CardEditButton({
  label,
  icon,
  outline = false,
  onClick,
  children = 'Bearbeiten',
}: {
  label?: string;
  icon: ReactNode;
  /** Umrandet, wenn der Knopf etwas anlegt statt zu ändern. */
  outline?: boolean;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('min-h-11', outline && 'border-line bg-paper border')}
      leftIcon={icon}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
